import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BOAPI } from '../service/UsersService';
import utils from '../utils/utils';
import { IMAGE_HINTS, shrinkImageFile } from '../utils/imageResize';
import ConfirmModal from './ConfirmModal';
import MapPicker from './MapPicker';

// תמונה יכולה להיות קישור חיצוני מלא (http/https) ולא רק נתיב /uploads/...
// מקומי - ראו הערה זהה ב-AnnouncementEditModal.jsx
const fullUrl = (path) => (!path ? null : /^https?:\/\//i.test(path) ? path : `${utils.BaseUrl}${path}`);

// שעות פתיחה - אותו מבנה JSON בדיוק כמו OpeningHoursEditor.tsx באפליקציה
// (day: 0=ראשון...6=שבת). Businesses.openingHours נשמר כטקסט JSON - כשהוא
// חוזר מ-spBOGetBusinesses הוא כבר string, צריך JSON.parse לפני עריכה;
// כשנשלח חזרה ב-@params צריך אובייקט/מערך ממש (לא string) כדי ש-JSON_QUERY
// בשרת יחלץ אותו נכון - ראו BOAddBusiness/BOUpdateBusiness ב-BO.sql
const DAY_LABELS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
const DEFAULT_OPEN_HOURS = [0, 1, 2, 3, 4, 5, 6].map((day) => ({
  day, isOpen: day <= 4, open: '10:00', close: '22:00',
}));

const emptyForm = () => ({
  name: '', description: '', logoUrl: '',
  address: '', city: '', lat: '', lng: '', phone: '', discountPercent: '', commissionPercent: '', benefitDescription: '', sortOrder: '0',
  leadStatus: 'pending',
});

// סטטוס תהליך הגיוס - ניתן לעריכה רק לפני שבעל העסק אישר בפועל (ראו
// isLeadStatusEditable למטה); 'confirmed' לא מופיע כאן כי admin לא קובע אותו
// ישירות - הוא נכנס רק דרך אישור הקישור בפועל (spConfirmBusinessOwnership)
const LEAD_STATUS_OPTIONS = [
  { key: 'pending', label: 'ממתין' },
  { key: 'in_progress', label: 'בתהליך' },
  { key: 'rejected', label: 'נדחה' },
];

const emptyNewUserForm = () => ({ phone: '', password: '', fName: '', lName: '' });

// הוספה/עריכה של בית עסק - business=null סוגר את המודל, business={} פותח
// אותו במצב יצירה, business={...} (רשומה קיימת) פותח במצב עריכה. אותו
// מודל לשני המצבים (כמו BusinessEditModal/adminBusinessEdit.tsx באפליקציה) -
// ההבדל היחיד הוא אם יש business.id (עריכה, BOUpdateBusiness) או לא
// (יצירה, BOAddBusiness). שגיאת שמירה מוצגת ליד כפתור השמירה (footer),
// לא למעלה - כדי שתהיה גלויה בלי לגלול. סגירה (backdrop/✕/ביטול) מזהה אם
// יש שינויים שלא נשמרו (isDirty, השוואה ל-snapshot שנלקח בפתיחה) ומבקשת
// אישור לפני איבוד המידע - אותה מוסכמה חוזרת גם ב-AnnouncementEditModal.jsx
export default function BusinessEditModal({ business, onClose, onSaved, existingProductNames }) {
  const isEdit = !!business?.id;
  const [form, setForm] = useState(emptyForm());
  const [openHours, setOpenHours] = useState(DEFAULT_OPEN_HOURS);
  const [galleryImages, setGalleryImages] = useState([]);
  const [products, setProducts] = useState([]);
  const [productInput, setProductInput] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [ownerLabel, setOwnerLabel] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [ownerSearch, setOwnerSearch] = useState('');
  const [ownerListOpen, setOwnerListOpen] = useState(false);
  const [showNewUserForm, setShowNewUserForm] = useState(false);
  const [newUserForm, setNewUserForm] = useState(emptyNewUserForm());
  const [creatingUser, setCreatingUser] = useState(false);
  // בית עסק יכול להשתייך למספר קטגוריות - ראו BusinessCategoryLinks ב-Tables.sql.
  // categoryOptions = קטלוג הקטגוריות הקיימות (לבחירה), selectedCategoryKeys = מה שנבחר בפועל
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategoryKeys, setSelectedCategoryKeys] = useState([]);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategoryLabel, setNewCategoryLabel] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);
  const [showOnMap, setShowOnMap] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingGalleryImage, setUploadingGalleryImage] = useState(false);
  const [error, setError] = useState('');
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const snapshotRef = useRef(null);

  useEffect(() => {
    if (!business) return;

    const initialForm = {
      name: business.name || '',
      description: business.description || '',
      logoUrl: business.logoUrl || '',
      address: business.address || '',
      city: business.city || '',
      lat: business.lat ?? '',
      lng: business.lng ?? '',
      phone: business.phone || '',
      discountPercent: business.discountPercent ?? '',
      commissionPercent: business.commissionPercent ?? '',
      benefitDescription: business.benefitDescription || '',
      sortOrder: String(business.sortOrder ?? 0),
      leadStatus: business.leadStatus || 'pending',
    };
    setForm(initialForm);
    const initialCategoryKeys = business.categories?.length ? business.categories : business.category ? [business.category] : [];
    setSelectedCategoryKeys(initialCategoryKeys);
    // תפתח מקופל כברירת מחדל - רק עסק שכבר יש לו נ"צ שמור נפתח עם המפה גלויה
    setShowOnMap(business.lat != null && business.lng != null);
    setOwnerId(business.ownerId ? String(business.ownerId) : '');
    setOwnerLabel(business.ownerName || '');
    setOwnerSearch('');
    setOwnerListOpen(false);
    setShowNewUserForm(false);
    setNewUserForm(emptyNewUserForm());
    setShowNewCategoryInput(false);
    setNewCategoryLabel('');
    setError('');

    let initialOpenHours = DEFAULT_OPEN_HOURS;
    if (business.openingHours) {
      try {
        const parsed = typeof business.openingHours === 'string' ? JSON.parse(business.openingHours) : business.openingHours;
        if (Array.isArray(parsed) && parsed.length === 7) initialOpenHours = parsed;
      } catch { /* keep default */ }
    }
    setOpenHours(initialOpenHours);

    let initialGallery = [];
    if (business.galleryImages) {
      try {
        const parsed = typeof business.galleryImages === 'string' ? JSON.parse(business.galleryImages) : business.galleryImages;
        if (Array.isArray(parsed)) initialGallery = parsed;
      } catch { /* keep empty */ }
    }
    setGalleryImages(initialGallery);

    // products כבר מגיע כמערך string[] אמיתי מהשרת (לא JSON טקסטואלי כמו
    // galleryImages/openingHours) - ה-Node מפרק שם productsRaw (STRING_AGG
    // מ-BusinessProducts) למערך לפני שהוא יוצא ללקוח, ראו attachProductsArray ב-actions.ts
    const initialProducts = Array.isArray(business.products) ? business.products : [];
    setProducts(initialProducts);
    setProductInput('');

    // snapshot לבדיקת "שינויים שלא נשמרו" - נלקח אחרי שכל השדות אותחלו
    snapshotRef.current = JSON.stringify({
      form: initialForm, ownerId: business.ownerId ? String(business.ownerId) : '',
      openHours: initialOpenHours, galleryImages: initialGallery, products: initialProducts,
      categories: [...initialCategoryKeys].sort(),
    });

    BOAPI.fetchUsers().then((resp) => {
      if (resp?.[0]?.[0]?.code === 0) setAllUsers(resp[1] || []);
    }).catch(() => {});

    BOAPI.fetchBusinessCategories().then((resp) => {
      if (resp?.[0]?.[0]?.code === 0) {
        const cats = resp[1] || [];
        setCategoryOptions(cats);
        // ברירת מחדל רק בעסק חדש (עוד אין לו אף קטגוריה נבחרת) - לא דורסת בחירה קיימת בעריכה
        setSelectedCategoryKeys((prev) => (prev.length > 0 ? prev : cats[0] ? [cats[0].key] : []));
      }
    }).catch(() => {});
  }, [business]);

  // נעילת גלילת הרקע כשהמודל פתוח - קריטי בספארי לאייפון: position:fixed
  // לבדו על ה-overlay לא מספיק, מחוות גלילה יכולות "לברוח" ולגלול את העמוד
  // שמתחת למודל במקום את תוכנו. position:fixed גם על ה-body (עם top שלילי
  // לפי מיקום הגלילה הנוכחי) היא הדרך היציבה שעובדת בפועל ב-iOS, לא רק
  // overflow:hidden. משוחזר בסגירה (כולל מיקום הגלילה המקורי)
  useEffect(() => {
    if (!business) return;
    const scrollY = window.scrollY;
    const original = {
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
    };
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.position = original.position;
      document.body.style.top = original.top;
      document.body.style.width = original.width;
      document.body.style.overflow = original.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [business]);

  // מכבים נ"צ -> מנקים lat/lng מהטופס (לא רק מסתירים ויזואלית), כך שגם
  // isDirty וגם handleSave רואים את זה כערך null רגיל בלי טיפול מיוחד -
  // מבטיח שבית עסק שבוטל לו הנ"צ באמת יפסיק להופיע במפה בשמירה
  const toggleShowOnMap = () => {
    setShowOnMap((prev) => {
      const next = !prev;
      if (!next) setForm((p) => ({ ...p, lat: '', lng: '' }));
      return next;
    });
  };

  // useMemo חייב לרוץ בכל רינדור (לא מותנה) - לפני ה-early return למטה,
  // אחרת מספר ה-hooks משתנה בין רינדור עם business=null לרינדור עם עסק
  // אמיתי, וגורם ל"Rendered more hooks than during the previous render"
  const filteredUsers = useMemo(() => {
    const q = ownerSearch.trim().toLowerCase();
    if (!q) return allUsers;
    return allUsers.filter((u) => `${u.fName} ${u.lName} ${u.phone}`.toLowerCase().includes(q));
  }, [allUsers, ownerSearch]);

  if (!business) return null;

  const isDirty = () => {
    if (!snapshotRef.current) return false;
    return JSON.stringify({ form, ownerId, openHours, galleryImages, products, categories: [...selectedCategoryKeys].sort() }) !== snapshotRef.current;
  };

  const attemptClose = () => {
    if (saving) return;
    if (isDirty()) {
      setShowCloseConfirm(true);
      return;
    }
    onClose();
  };

  const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const updateDay = (day, patch) => {
    setOpenHours((prev) => prev.map((d) => (d.day === day ? { ...d, ...patch } : d)));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    setError('');
    try {
      const resp = await utils.uploadFile(await shrinkImageFile(file, 'logo'), 'business-logos');
      if (!resp?.url) throw new Error('Upload response missing url');
      setForm((prev) => ({ ...prev, logoUrl: resp.url }));
    } catch {
      setError('העלאת הלוגו נכשלה, נסו שוב');
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  // multiple - מאפשר לבחור כמה תמונות בבת אחת בחלון הבחירה (לא רק קובץ אחד
  // כל פעם); מעלה כל קובץ בנפרד ברצף (לא Promise.all) כדי לא להציף את
  // השרת בבקשות מקבילות ולהוסיף כל תמונה לגלריה בדיוק לפי סדר הבחירה
  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploadingGalleryImage(true);
    setError('');
    try {
      for (const file of files) {
        const resp = await utils.uploadFile(await shrinkImageFile(file, 'gallery'), 'business-gallery');
        if (!resp?.url) throw new Error('Upload response missing url');
        setGalleryImages((prev) => [...prev, resp.url]);
      }
    } catch {
      setError('העלאת אחת התמונות נכשלה, נסו שוב');
    } finally {
      setUploadingGalleryImage(false);
      e.target.value = '';
    }
  };

  const removeGalleryImage = (url) => setGalleryImages((prev) => prev.filter((u) => u !== url));

  // רשימת המוצרים שהעסק מוכר - להצגת הכפתור "מסננים לפי מוצר" באפליקציה
  // (businesses.tsx). הוספה בלחיצה על Enter/פסיק, בלי כפילויות (case-insensitive) -
  // ראו הערה ב-BusinessProducts ב-Tables.sql
  const addProduct = (raw) => {
    const name = raw.trim();
    if (!name) return;
    setProducts((prev) => (prev.some((p) => p.toLowerCase() === name.toLowerCase()) ? prev : [...prev, name]));
    setProductInput('');
  };
  const removeProduct = (name) => setProducts((prev) => prev.filter((p) => p !== name));
  const onProductInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addProduct(productInput);
    } else if (e.key === 'Backspace' && !productInput && products.length > 0) {
      setProducts((prev) => prev.slice(0, -1));
    }
  };

  const selectOwner = (user) => {
    setOwnerId(String(user.id));
    setOwnerLabel(`${user.fName} ${user.lName} (${user.phone})`);
    setOwnerListOpen(false);
  };

  const submitNewUser = async () => {
    if (!newUserForm.phone.trim() || !newUserForm.password.trim()) {
      setError('טלפון וסיסמה הם שדות חובה ליצירת משתמש חדש');
      return;
    }
    if (!/^\d{9,11}$/.test(newUserForm.phone.trim())) {
      setError('מספר טלפון לא תקין - ספרות בלבד, בין 9 ל-11 ספרות');
      return;
    }
    setCreatingUser(true);
    setError('');
    try {
      const resp = await BOAPI.createUser({
        phone: newUserForm.phone.trim(),
        password: newUserForm.password,
        fName: newUserForm.fName.trim(),
        lName: newUserForm.lName.trim(),
      });
      if (resp?.[0]?.[0]?.code === 0) {
        const created = resp[1]?.[0];
        const newUser = { id: created.id, fName: created.fName, lName: created.lName, phone: created.phone };
        setAllUsers((prev) => [newUser, ...prev]);
        selectOwner(newUser);
        setShowNewUserForm(false);
        setNewUserForm(emptyNewUserForm());
      } else {
        setError(resp?.[0]?.[0]?.msg || 'יצירת המשתמש נכשלה');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setCreatingUser(false);
    }
  };

  const submitNewCategory = async () => {
    if (!newCategoryLabel.trim()) {
      setError('שם הקטגוריה הוא שדה חובה');
      return;
    }
    setAddingCategory(true);
    setError('');
    try {
      const resp = await BOAPI.addBusinessCategory({ label: newCategoryLabel.trim() });
      if (resp?.[0]?.[0]?.code === 0) {
        const created = resp[1]?.[0];
        setCategoryOptions((prev) => [...prev, created]);
        setSelectedCategoryKeys((prev) => (prev.includes(created.key) ? prev : [...prev, created.key]));
        setShowNewCategoryInput(false);
        setNewCategoryLabel('');
      } else {
        setError(resp?.[0]?.[0]?.msg || 'הוספת הקטגוריה נכשלה');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setAddingCategory(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('שם העסק הוא שדה חובה');
      return;
    }
    if (selectedCategoryKeys.length === 0) {
      setError('יש לבחור לפחות קטגוריה אחת');
      return;
    }
    const discount = Number(form.discountPercent);
    if (!form.discountPercent || isNaN(discount) || discount < 1 || discount > 99) {
      setError('אחוז ההנחה חייב להיות מספר בין 1 ל-99');
      return;
    }
    const commission = Number(form.commissionPercent);
    if (form.commissionPercent === '' || isNaN(commission) || commission < 0 || commission > 100) {
      setError('אחוז עמלת הסליקה חייב להיות מספר בין 0 ל-100');
      return;
    }
    // בעלים אופציונלי בעסק שעוד לא אושר - עובד פותח עסק לפני הפגישה, והמשתמש
    // נוצר אוטומטית כשהבעלים מאשר את הקישור. עסק שכבר אושר חייב להישאר עם בעלים
    if (!ownerId && business.ownerConfirmedAt) {
      setError('יש לבחור בעל עסק');
      return;
    }
    const latNum = form.lat === '' ? null : Number(form.lat);
    const lngNum = form.lng === '' ? null : Number(form.lng);
    if ((latNum !== null && (isNaN(latNum) || latNum < -90 || latNum > 90)) ||
        (lngNum !== null && (isNaN(lngNum) || lngNum < -180 || lngNum > 180))) {
      setError('נ"צ לא תקין - קו רוחב בין 90- ל-90, קו אורך בין 180- ל-180');
      return;
    }
    const sortOrderNum = Number(form.sortOrder);

    setSaving(true);
    setError('');
    try {
      const params = {
        ...(isEdit ? { id: business.id } : {}),
        name: form.name.trim(),
        categories: selectedCategoryKeys,
        description: form.description.trim() || null,
        logoUrl: form.logoUrl || null,
        galleryImages,
        address: form.address.trim() || null,
        city: form.city.trim() || null,
        lat: latNum,
        lng: lngNum,
        phone: form.phone.trim() || null,
        discountPercent: discount,
        commissionPercent: commission,
        benefitDescription: form.benefitDescription.trim() || null,
        openingHours: openHours,
        sortOrder: form.sortOrder === '' || isNaN(sortOrderNum) ? 0 : sortOrderNum,
        ownerId: ownerId ? Number(ownerId) : null,
        products,
        leadStatus: form.leadStatus,
      };
      const resp = isEdit ? await BOAPI.updateBusiness(params) : await BOAPI.addBusiness(params);
      if (resp?.[0]?.[0]?.code === 0) {
        onSaved(resp[1]?.[0]);
      } else {
        setError(resp?.[0]?.[0]?.msg || 'שמירת בית העסק נכשלה');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={s.overlay} onClick={attemptClose}>
      <div style={s.card} onClick={(e) => e.stopPropagation()}>
        <div style={s.header}>
          <h3 style={s.headerTitle}>{isEdit ? `עריכת ${business.name}` : 'בית עסק חדש'}</h3>
          <button style={s.closeBtn} onClick={attemptClose} disabled={saving}>✕</button>
        </div>

        <div style={s.body}>
          <div style={s.logoRow}>
            {form.logoUrl ? (
              <img src={fullUrl(form.logoUrl)} alt="" style={s.logoPreview} />
            ) : (
              <div style={s.logoPlaceholder}>{(form.name || '?')[0]}</div>
            )}
            <label style={s.uploadBtn}>
              {uploadingLogo ? 'מעלה...' : '📷 העלאת לוגו'}
              <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} disabled={uploadingLogo} />
            </label>
            <span style={s.imageHint}>{IMAGE_HINTS.logo}</span>
          </div>

          <div style={s.grid}>
            <Field label="שם העסק *" value={form.name} onChange={setField('name')} />

            <div style={s.fieldGroup}>
              <label style={s.fieldLabel}>סטטוס</label>
              {business.ownerConfirmedAt ? (
                <div style={s.leadStatusConfirmed}>✅ פעיל - אושר ע"י בעל העסק</div>
              ) : (
                <select style={s.fieldInput} value={form.leadStatus} onChange={setField('leadStatus')}>
                  {LEAD_STATUS_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              )}
            </div>

            <div style={{ ...s.fieldGroup, gridColumn: '1 / -1' }}>
              <label style={s.fieldLabel}>קטגוריות * (ניתן לבחור כמה)</label>
              {!showNewCategoryInput ? (
                <div style={s.categoryChipsBox}>
                  {categoryOptions.map((c) => {
                    const selected = selectedCategoryKeys.includes(c.key);
                    return (
                      <button
                        type="button"
                        key={c.key}
                        style={{ ...s.categoryChip, ...(selected ? s.categoryChipActive : {}) }}
                        onClick={() => setSelectedCategoryKeys((prev) => (selected ? prev.filter((k) => k !== c.key) : [...prev, c.key]))}
                      >
                        {selected ? '✓ ' : ''}{c.label}
                      </button>
                    );
                  })}
                  <button type="button" style={s.smallGhostBtn} onClick={() => setShowNewCategoryInput(true)}>+ חדשה</button>
                </div>
              ) : (
                <div style={s.categoryPickRow}>
                  <input
                    type="text"
                    value={newCategoryLabel}
                    onChange={(e) => setNewCategoryLabel(e.target.value)}
                    style={s.fieldInput}
                    placeholder="שם קטגוריה חדשה"
                    autoFocus
                  />
                  <button type="button" style={s.smallPrimaryBtn} onClick={submitNewCategory} disabled={addingCategory}>
                    {addingCategory ? '...' : 'הוספה'}
                  </button>
                  <button type="button" style={s.smallGhostBtn} onClick={() => { setShowNewCategoryInput(false); setNewCategoryLabel(''); }}>ביטול</button>
                </div>
              )}
            </div>

            <Field label="אחוז הנחה (1-99) *" value={form.discountPercent} onChange={setField('discountPercent')} type="number" />
            <Field label="אחוז עמלת סליקה (0-100) *" value={form.commissionPercent} onChange={setField('commissionPercent')} type="number" />
            <Field label="סדר תצוגה" value={form.sortOrder} onChange={setField('sortOrder')} type="number" />

            <div style={{ ...s.fieldGroup, gridColumn: '1 / -1' }}>
              <label style={s.fieldLabel}>{business.ownerConfirmedAt ? 'בעל העסק *' : 'בעל העסק (אופציונלי)'}</label>
              {!showNewUserForm ? (
                <div style={s.ownerPickerWrap}>
                  <button type="button" style={s.ownerPickerBtn} onClick={() => setOwnerListOpen((v) => !v)}>
                    <span>{ownerLabel || '— בחירת משתמש —'}</span>
                    <span>{ownerListOpen ? '▲' : '▼'}</span>
                  </button>
                  {ownerListOpen && (
                    <div style={s.ownerDropdown}>
                      <input
                        type="text"
                        value={ownerSearch}
                        onChange={(e) => setOwnerSearch(e.target.value)}
                        style={s.ownerSearchInput}
                        placeholder="חיפוש לפי שם או טלפון..."
                        autoFocus
                      />
                      <div style={s.ownerList}>
                        {filteredUsers.length === 0 ? (
                          <div style={s.ownerEmptyText}>לא נמצאו משתמשים</div>
                        ) : (
                          filteredUsers.map((u) => (
                            <button type="button" key={u.id} style={s.ownerOption} onClick={() => selectOwner(u)}>
                              {u.fName} {u.lName} <span style={s.ownerOptionUsername}>({u.phone})</span>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                  {!ownerId && !business.ownerConfirmedAt && (
                    <span style={s.hint}>אפשר להשאיר ריק: אחרי שתשלחו לבעל העסק את הקישור ("העתקת קישור" בטבלת בתי העסק) וימלא ויאשר, המשתמש שלו ייווצר אוטומטית והעסק יעלה לאפליקציה.</span>
                  )}
                  <button type="button" style={s.newUserToggleBtn} onClick={() => { setShowNewUserForm(true); setOwnerListOpen(false); }}>
                    + הוספת משתמש חדש
                  </button>
                </div>
              ) : (
                <div style={s.newUserCard}>
                  <div style={s.newUserGrid}>
                    <input type="tel" value={newUserForm.phone} onChange={(e) => setNewUserForm((p) => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 11) }))} style={s.fieldInput} placeholder="טלפון * (זה גם שם ההתחברות)" />
                    <input type="password" value={newUserForm.password} onChange={(e) => setNewUserForm((p) => ({ ...p, password: e.target.value }))} style={s.fieldInput} placeholder="סיסמה *" />
                    <input type="text" value={newUserForm.fName} onChange={(e) => setNewUserForm((p) => ({ ...p, fName: e.target.value }))} style={s.fieldInput} placeholder="שם פרטי" />
                    <input type="text" value={newUserForm.lName} onChange={(e) => setNewUserForm((p) => ({ ...p, lName: e.target.value }))} style={s.fieldInput} placeholder="שם משפחה" />
                  </div>
                  <p style={s.newUserHint}>המשתמש ייווצר עם תפקיד "בעל עסק" (owner) - הוא זה שיוכל לאשר קודי קופון ולנהל את פרטי בית העסק הזה מהאפליקציה.</p>
                  <div style={s.newUserActions}>
                    <button type="button" style={s.smallPrimaryBtn} onClick={submitNewUser} disabled={creatingUser}>
                      {creatingUser ? 'יוצר...' : 'יצירה ושיוך כבעלים'}
                    </button>
                    <button type="button" style={s.smallGhostBtn} onClick={() => { setShowNewUserForm(false); setNewUserForm(emptyNewUserForm()); }}>ביטול</button>
                  </div>
                </div>
              )}
            </div>

            <Field label="עיר" value={form.city} onChange={setField('city')} />
            <Field label="כתובת" value={form.address} onChange={setField('address')} />
            <Field label="טלפון" value={form.phone} onChange={setField('phone')} />
          </div>

          <Field label="תיאור" value={form.description} onChange={setField('description')} textarea />
          <Field label="פירוט ההטבה" value={form.benefitDescription} onChange={setField('benefitDescription')} placeholder='לדוגמה: "לא כולל פריטים במבצע"' />

          <div style={s.fieldGroup}>
            <label style={s.fieldLabel}>מוצרים שנמכרים בחנות</label>
            <div style={s.productsTagBox}>
              {products.map((name) => (
                <span key={name} style={s.productTag}>
                  {name}
                  <button type="button" style={s.productTagRemove} onClick={() => removeProduct(name)}>✕</button>
                </span>
              ))}
              <input
                type="text"
                value={productInput}
                onChange={(e) => setProductInput(e.target.value)}
                onKeyDown={onProductInputKeyDown}
                onBlur={() => addProduct(productInput)}
                style={s.productInput}
                placeholder={products.length === 0 ? 'לדוגמה: חולצה, נעליים...' : 'הוספת מוצר...'}
                list="existing-product-names"
              />
            </div>
            <span style={s.hint}>Enter או פסיק להוספה - מוצג כפילטר חיפוש באפליקציה, ליד עיר ואחוז הנחה</span>
            {existingProductNames?.length > 0 && (
              <datalist id="existing-product-names">
                {existingProductNames.map((name) => <option key={name} value={name} />)}
              </datalist>
            )}
          </div>

          <div style={s.fieldGroup}>
            <label style={s.fieldLabel}>תמונות רקע/גלריה (מוצגות במסך הפרטים באפליקציה)</label>
            <div style={{ ...s.imageHint, marginBottom: 8 }}>{IMAGE_HINTS.gallery}</div>
            <div style={s.galleryRow}>
              {galleryImages.map((url) => (
                <div key={url} style={s.galleryThumbWrap}>
                  <img src={fullUrl(url)} alt="" style={s.galleryThumb} />
                  <button type="button" style={s.galleryRemoveBtn} onClick={() => removeGalleryImage(url)}>✕</button>
                </div>
              ))}
              <label style={s.galleryAddBtn}>
                {uploadingGalleryImage ? '...' : '+'}
                <input type="file" accept="image/*" multiple onChange={handleGalleryUpload} style={{ display: 'none' }} disabled={uploadingGalleryImage} />
              </label>
            </div>
          </div>

          <div style={s.fieldGroup}>
            <label style={s.fieldLabel}>שעות פתיחה</label>
            <div style={s.hoursCard}>
              {openHours.map((d) => (
                <div key={d.day} style={s.hoursRow}>
                  <label style={s.hoursToggle}>
                    <input type="checkbox" checked={d.isOpen} onChange={(e) => updateDay(d.day, { isOpen: e.target.checked })} />
                    {DAY_LABELS[d.day]}
                  </label>
                  {d.isOpen ? (
                    <div style={s.hoursTimes}>
                      <input type="time" value={d.open} onChange={(e) => updateDay(d.day, { open: e.target.value })} style={s.timeInput} />
                      <span>—</span>
                      <input type="time" value={d.close} onChange={(e) => updateDay(d.day, { close: e.target.value })} style={s.timeInput} />
                    </div>
                  ) : (
                    <span style={s.closedText}>סגור</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div style={s.fieldGroup}>
            <label style={s.checkboxRow}>
              <input type="checkbox" checked={showOnMap} onChange={toggleShowOnMap} />
              הצגת בית העסק גם במסך "בתי עסק על המפה" באפליקציה
            </label>
            {showOnMap && (
              <>
                <label style={{ ...s.fieldLabel, marginTop: 10 }}>נ"צ למפה</label>
                <MapPicker
                  lat={form.lat === '' ? null : Number(form.lat)}
                  lng={form.lng === '' ? null : Number(form.lng)}
                  onChange={(newLat, newLng) => setForm((prev) => ({ ...prev, lat: String(newLat), lng: String(newLng) }))}
                />
                <div style={s.grid}>
                  <Field label="קו רוחב (lat)" value={form.lat} onChange={setField('lat')} type="number" placeholder="לדוגמה 31.7683" />
                  <Field label="קו אורך (lng)" value={form.lng} onChange={setField('lng')} type="number" placeholder="לדוגמה 35.2137" />
                </div>
              </>
            )}
          </div>
        </div>

        <div style={s.footer}>
          {error && <div style={s.errorBanner}>❌ {error}</div>}
          <div style={s.footerBtns}>
            <button style={s.cancelBtn} onClick={attemptClose} disabled={saving}>ביטול</button>
            <button style={{ ...s.saveBtn, ...(saving ? s.btnDisabled : {}) }} onClick={handleSave} disabled={saving}>
              {saving ? 'שומר...' : isEdit ? 'שמירת שינויים' : 'הוספת בית עסק'}
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        visible={showCloseConfirm}
        title="שינויים שלא נשמרו"
        message="יש שינויים שלא נשמרו. לסגור בכל זאת?"
        confirmLabel="סגירה בלי לשמור"
        danger
        onConfirm={() => { setShowCloseConfirm(false); onClose(); }}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', textarea, placeholder }) {
  return (
    <div style={s.fieldGroup}>
      <label style={s.fieldLabel}>{label}</label>
      {textarea ? (
        <textarea value={value} onChange={onChange} style={{ ...s.fieldInput, minHeight: 70, resize: 'vertical' }} placeholder={placeholder} />
      ) : (
        <input type={type} value={value} onChange={onChange} style={s.fieldInput} placeholder={placeholder} />
      )}
    </div>
  );
}

const s = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 200, padding: 16, direction: 'rtl',
  },
  card: {
    width: '100%', maxWidth: 640, maxHeight: '90vh', overflow: 'hidden',
    background: '#fff', borderRadius: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
    display: 'flex', flexDirection: 'column',
  },
  // flexShrink:0 על header/footer + flex:1 + minHeight:0 על body - כך רק
  // ה-body גולל (לא גם ה-card מסביבו, מניעת גלילה מקוננת שגם היא תורמת
  // לבאג הגלילה ב-iOS) וה-header/footer (כולל כפתור השמירה) תמיד נשארים
  // גלויים במקום לגלול יחד עם שאר הטופס
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', borderBottom: '1px solid #e3e8ee', flexShrink: 0 },
  headerTitle: { margin: 0, fontSize: 17, fontWeight: 800, color: '#1a1f36' },
  closeBtn: { background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 18, color: '#697386' },
  body: { padding: 22, display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto', flex: 1, minHeight: 0, WebkitOverflowScrolling: 'touch' },

  logoRow: { display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' },
  logoPreview: { width: 64, height: 64, borderRadius: 12, objectFit: 'cover', border: '1px solid #e3e8ee' },
  logoPlaceholder: { width: 64, height: 64, borderRadius: 12, background: '#EEF2FF', color: '#1f6feb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 24 },
  imageHint: { color: '#8792a2', fontSize: 11.5, lineHeight: 1.5 },
  uploadBtn: { padding: '9px 16px', background: '#EEF2FF', color: '#1f6feb', border: '1px solid #c7d7fb', borderRadius: 10, cursor: 'pointer', fontSize: 12.5, fontWeight: 700 },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: 5 },
  fieldLabel: { fontSize: 12, fontWeight: 700, color: '#4f566b' },
  fieldInput: { padding: '9px 12px', borderRadius: 10, border: '1.5px solid #e3e8ee', fontSize: 13.5, boxSizing: 'border-box', direction: 'rtl', fontFamily: 'inherit', width: '100%' },
  leadStatusConfirmed: { padding: '9px 12px', borderRadius: 10, background: '#e8f5e9', color: '#2e7d32', fontSize: 13, fontWeight: 700 },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: '#1a1f36', cursor: 'pointer' },

  categoryPickRow: { display: 'flex', gap: 8, alignItems: 'center' },
  categoryChipsBox: { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' },
  categoryChip: {
    padding: '7px 14px', borderRadius: 999, border: '1.5px solid #e3e8ee', background: '#fff',
    color: '#4f566b', cursor: 'pointer', fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit',
  },
  categoryChipActive: { background: '#EEF2FF', borderColor: '#1f6feb', color: '#1f6feb' },
  smallGhostBtn: { padding: '8px 12px', background: '#fff', color: '#4f566b', border: '1.5px solid #e3e8ee', borderRadius: 9, cursor: 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' },
  smallPrimaryBtn: { padding: '8px 14px', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' },

  hint: { fontSize: 11, color: '#8792a2' },
  productsTagBox: {
    display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
    padding: '8px 10px', borderRadius: 10, border: '1.5px solid #e3e8ee', minHeight: 42, boxSizing: 'border-box',
  },
  productTag: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: '#EEF2FF', color: '#1f6feb', borderRadius: 999, padding: '4px 6px 4px 10px', fontSize: 12.5, fontWeight: 700,
  },
  productTagRemove: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#1f6feb', fontSize: 11, padding: 2, lineHeight: 1 },
  productInput: { flex: 1, minWidth: 120, border: 'none', outline: 'none', fontSize: 13.5, direction: 'rtl', fontFamily: 'inherit' },

  ownerPickerWrap: { position: 'relative' },
  ownerPickerBtn: {
    width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '9px 12px', borderRadius: 10, border: '1.5px solid #e3e8ee', background: '#fff',
    fontSize: 13.5, color: '#1a1f36', cursor: 'pointer', boxSizing: 'border-box', fontFamily: 'inherit',
  },
  ownerDropdown: {
    marginTop: 6, border: '1.5px solid #e3e8ee', borderRadius: 10, background: '#fff',
    boxShadow: '0 8px 24px rgba(0,0,0,0.1)', padding: 10, position: 'relative', zIndex: 5,
  },
  ownerSearchInput: { width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e3e8ee', fontSize: 13, boxSizing: 'border-box', direction: 'rtl', marginBottom: 8 },
  ownerList: { maxHeight: 160, overflowY: 'auto', display: 'flex', flexDirection: 'column' },
  ownerOption: { textAlign: 'right', padding: '8px 10px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 13, color: '#1a1f36', borderRadius: 6, fontFamily: 'inherit' },
  ownerOptionUsername: { color: '#a3acb9', fontSize: 11.5 },
  ownerEmptyText: { padding: '10px 4px', color: '#a3acb9', fontSize: 12.5, textAlign: 'center' },
  newUserToggleBtn: { marginTop: 8, padding: '8px 12px', background: '#ECFDF5', color: '#16A34A', border: '1px solid #bbf0d1', borderRadius: 9, cursor: 'pointer', fontSize: 12, fontWeight: 700 },

  newUserCard: { border: '1.5px dashed #c7d7fb', borderRadius: 12, padding: 14, background: '#F8FAFF' },
  newUserGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 8 },
  newUserHint: { fontSize: 11.5, color: '#8792a2', lineHeight: 1.5, margin: '0 0 10px' },
  newUserActions: { display: 'flex', gap: 8 },

  galleryRow: { display: 'flex', flexWrap: 'wrap', gap: 10 },
  galleryThumbWrap: { position: 'relative', width: 100, height: 68 },
  galleryThumb: { width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10, border: '1px solid #e3e8ee' },
  galleryRemoveBtn: {
    position: 'absolute', top: -6, left: -6, width: 20, height: 20, borderRadius: 10,
    background: '#c62828', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 11, lineHeight: '20px', padding: 0,
  },
  galleryAddBtn: {
    width: 100, height: 68, borderRadius: 10, border: '1.5px dashed #c7d7fb', background: '#F8FAFF',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, color: '#1f6feb', cursor: 'pointer',
  },

  hoursCard: { border: '1px solid #e3e8ee', borderRadius: 12, padding: '6px 14px', display: 'flex', flexDirection: 'column' },
  hoursRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f2f5' },
  hoursToggle: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: '#1a1f36' },
  hoursTimes: { display: 'flex', alignItems: 'center', gap: 6 },
  timeInput: { padding: '5px 8px', borderRadius: 8, border: '1.5px solid #e3e8ee', fontSize: 12.5 },
  closedText: { fontSize: 12.5, color: '#a3acb9' },

  footer: { padding: '14px 22px', borderTop: '1px solid #e3e8ee', display: 'flex', flexDirection: 'column', gap: 10, flexShrink: 0 },
  errorBanner: { background: '#ffebee', color: '#c62828', padding: 12, borderRadius: 8, fontSize: 13 },
  footerBtns: { display: 'flex', gap: 10 },
  cancelBtn: { flex: 1, padding: '11px 16px', background: '#fff', color: '#4f566b', border: '1.5px solid #e3e8ee', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700 },
  saveBtn: { flex: 1, padding: '11px 16px', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700 },
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
};
